import React from 'react';
import * as Mui from '@mui/material';
import { withStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import type { WithStyles } from "type/tss";
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { RootState } from 'redux/store';
import { styles } from './style';
import { User } from 'model/user';
import { Agent } from 'model/agent';
import * as Icons from "react-feather";
import * as env from "shared/env";
import { Avatar } from "component/shared/avatar";
import { Dialog } from 'component/shared/dialog';
import { DialogTitle } from 'component/shared/dialog/title';
import { PersonalInfo } from './personal-info';
import { Photo } from './photo';
import { Login } from './login';
import { CopyID } from "component/shared/copy-id";
interface Props extends OwnProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {
	theme: Mui.Theme;
}

interface OwnProps {
	user: User;
	agent: Agent;
}

interface State {
	dialog?: "info" | "login" | "photo";
	dialogTitle: string;
}

const mapStateToProps = (_state: RootState, _ownProps: OwnProps) => ({});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators({}, dispatch);

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { dialogTitle: "" };
	}

	private dialogContent() {
		const { dialog } = this.state;
		switch(dialog) {
			case "photo":
				return <Photo onClose={() => this.setState({ dialog: undefined })} agent={this.props.agent} />;
			case "info":
				return <PersonalInfo onClose={() => this.setState({ dialog: undefined })} agent={this.props.agent} />;
			case "login":
				return <Login onClose={() => this.setState({ dialog: undefined })} agent={this.props.agent} />;
			default:
				return;
		}
	}

	private renderEditButton(onClick: () => void) {
		const { classes = {} } = this.props;
		return (
			<Mui.Button className={classes.editButton} onClick={onClick} startIcon={<Icons.Edit2 size={16} />}>
				Edit
			</Mui.Button>
		);
	}

	public render() {
		const { agent, classes = {}, user, theme } = this.props;
		const { dialogTitle, dialog } = this.state;
		const canEdit = user && (user.admin || user.agentId === agent.id);

		return (
			<>
				{dialog && (
					<Dialog open onClose={() => this.setState({ dialog: undefined })} PaperProps={{ className: classes.dialog }}>
						<DialogTitle title={dialogTitle} onClose={() => this.setState({ dialog: undefined })} />
						<Mui.Divider className={classes.titleDivider} />
						<Mui.DialogContent dividers={false}>
							{this.dialogContent()}
						</Mui.DialogContent>
					</Dialog>
				)}

				<Mui.Grid container direction="column" spacing={3}>

					{/* Profile Photo */}
					<Mui.Grid item>
						<Mui.Box display="flex" justifyContent="space-between" alignItems="center">
							<Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
								<Mui.Typography className={classes.sectionTitle}>Profile Photo</Mui.Typography>
								<Mui.Tooltip title="Appears on agent roster & page">
									<Icons.Info size={16} color="#5F6368" style={{ cursor: "default" }} />
								</Mui.Tooltip>
							</Mui.Box>
							{canEdit && this.renderEditButton(() => this.setState({ dialog: "photo", dialogTitle: "Edit Profile Photo" }))}
						</Mui.Box>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Box display="flex" alignItems="center" style={{ gap: 16 }}>
							<Avatar
								phrase={agent.label}
								src={agent.photoUrl}
								diameter={64}
								color={user.agentId === agent.id ? theme.palette.secondary.main : theme.palette.primary.main}
							/>
							<Mui.Box>
								<Mui.Typography className={classes.agentName}>{agent.name}</Mui.Typography>
								<Mui.Typography className={classes.agentDesignation}>{agent.designation}</Mui.Typography>
							</Mui.Box>
						</Mui.Box>
					</Mui.Grid>

					<Mui.Grid item><Mui.Divider /></Mui.Grid>

					{/* Personal Info */}
					<Mui.Grid item>
						<Mui.Box display="flex" justifyContent="space-between" alignItems="center">
							<Mui.Typography className={classes.sectionTitle}>Personal Info</Mui.Typography>
							{canEdit && this.renderEditButton(() => this.setState({ dialog: "info", dialogTitle: "Edit Personal Info" }))}
						</Mui.Box>
					</Mui.Grid>
					<Mui.Grid item>
						<div className={classes.fieldsContainer}>
							<div className={classes.fieldRow}>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Name</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.name || "–"}</Mui.Typography>
								</div>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Title / Designation</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.designation || "–"}</Mui.Typography>
								</div>
							</div>
							<div className={classes.fieldRow}>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Email</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.email || "–"}</Mui.Typography>
								</div>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Cell Phone</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.cellPhone || "–"}</Mui.Typography>
								</div>
							</div>
							<div className={classes.fieldRow}>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Office Phone</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.officePhone || "–"}</Mui.Typography>
								</div>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Home Phone</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.homePhone || "–"}</Mui.Typography>
								</div>
							</div>
							<div className={classes.fieldRow}>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Address</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.officeAddress || "–"}</Mui.Typography>
								</div>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>City</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.officeCity || "–"}</Mui.Typography>
								</div>
							</div>
							<div className={classes.fieldRow}>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>State</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.officeState || "–"}</Mui.Typography>
								</div>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Zip</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.officeZip || "–"}</Mui.Typography>
								</div>
							</div>
						</div>
					</Mui.Grid>

					<Mui.Grid item><Mui.Divider /></Mui.Grid>

					{/* User Information */}
					<Mui.Grid item>
						<Mui.Box display="flex" justifyContent="space-between" alignItems="center">
							<Mui.Typography className={classes.sectionTitle}>User Information</Mui.Typography>
							{canEdit && this.renderEditButton(() => this.setState({ dialog: "login", dialogTitle: "Edit Account Information" }))}
						</Mui.Box>
					</Mui.Grid>
					<Mui.Grid item>
						<div className={classes.fieldsContainer}>
							<div className={classes.fieldRow}>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Username</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>{agent.username || "–"}</Mui.Typography>
								</div>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>Password</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>**********</Mui.Typography>
								</div>
							</div>
							<div className={classes.fieldRow}>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>User Level</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>
										{agent.admin ? (
											<Mui.Tooltip title="Administrators can view and edit all leads and settings within this account.">
												<span className={classes.adminText}>Admin</span>
											</Mui.Tooltip>
										) : "Agent"}
									</Mui.Typography>
								</div>
								<div className={classes.fieldCol}>
									<Mui.Typography className={classes.fieldLabel}>ID</Mui.Typography>
									<Mui.Typography className={classes.fieldValue}>
										<CopyID id={agent.id} text="Copy ID for lead importer" />
									</Mui.Typography>
								</div>
							</div>
						</div>
					</Mui.Grid>

				</Mui.Grid>
			</>
		);
	}
}

// tss-react's withStyles has no `withTheme` option; inject the theme as an own prop above `connect`
// (which forwards own props) so the class component keeps reading `this.props.theme`.
const Connected = connect(mapStateToProps, mapDispatchToProps)(Component);

const ThemedInfo = (props: OwnProps & WithStyles<typeof styles>) => {
	const theme = useTheme();
	return <Connected {...props} theme={theme} />;
};

export const Info = withStyles(ThemedInfo, styles);
